/**
 * A LIVE CHART, AS A PNG.
 *
 * Used twice: the per-panel "PNG" action, and the PDF export, whose charts have
 * to be the ACTUAL current charts rather than placeholders.
 *
 * ── WHY NOT html2canvas ──
 *
 * The reports screen's PDF path hands `#pie-chart` to html2canvas, which
 * rasterises the DOM by re-implementing CSS layout in JavaScript. It is slow, it
 * handles inline SVG poorly, and it depends on the node still being on screen
 * and unclipped. Recharts draws SVG — so the exact picture is already there, and
 * serialising it is both faster and lossless.
 *
 * ── THE ONE THING THAT HAS TO BE DONE BY HAND ──
 *
 * An `<svg>` serialised out of the document loses every style it was inheriting
 * from a stylesheet: emotion writes `fill` and `font-family` into CSS classes,
 * and the class names mean nothing once the markup is inside an `<img>`. So the
 * computed value of the properties that matter is written onto each node as a
 * presentation attribute BEFORE serialising. Skip that and the export is a black
 * chart on a transparent ground — which is exactly what a naive
 * `XMLSerializer` round-trip produces.
 */
import { palette } from '../../../../../style-utils/theme';

/** The properties that decide what a chart looks like. Copied onto each node. */
const INLINE_PROPS = [
  'fill',
  'fill-opacity',
  'stroke',
  'stroke-width',
  'stroke-opacity',
  'stroke-dasharray',
  'stroke-linecap',
  'stroke-linejoin',
  'font-family',
  'font-size',
  'font-weight',
  'text-anchor',
  'opacity',
  'letter-spacing',
];

const inlineStyles = (source: Element, clone: Element): void => {
  const computed = window.getComputedStyle(source);
  const declarations: string[] = [];
  for (const prop of INLINE_PROPS) {
    const value = computed.getPropertyValue(prop);
    if (value && value !== 'none' && value !== 'normal') {
      declarations.push(`${prop}:${value}`);
    }
  }
  if (declarations.length) clone.setAttribute('style', declarations.join(';'));

  const sourceChildren = Array.from(source.children);
  const cloneChildren = Array.from(clone.children);
  for (let i = 0; i < sourceChildren.length; i++) {
    if (cloneChildren[i]) inlineStyles(sourceChildren[i], cloneChildren[i]);
  }
};

/**
 * Any `<defs>` the chart references but does not contain.
 *
 * The gradients live in one document-level `<ChartDefs/>` rather than being
 * minted per chart — which is right for the screen and wrong for a serialised
 * fragment, because `url(#dash-area)` resolves against the DOCUMENT and there is
 * no document inside an `<img>`. So they are copied in.
 */
const adoptSharedDefs = (clone: SVGSVGElement): void => {
  const shared = document.querySelector('[data-testid="chart-defs"] defs');
  if (!shared) return;
  const imported = shared.cloneNode(true) as SVGDefsElement;
  clone.insertBefore(imported, clone.firstChild);
};

export interface PngOptions {
  /** 2 gives a chart that is still sharp in a PDF and on a retina screen. */
  scale?: number;
  /** SVG has no background of its own; without this a PNG is transparent and a
   *  dark viewer renders white axis labels on black. */
  background?: string;
}

/**
 * Serialise a live `<svg>` into a PNG blob.
 *
 * Rejects rather than resolving to a blank image if the node cannot be measured:
 * an export that silently produces an empty picture is the failure mode this
 * whole pass exists to remove.
 */
export const svgToPng = async (
  svg: SVGSVGElement | null,
  options: PngOptions = {}
): Promise<Blob> => {
  if (!svg) throw new Error('There is no chart to export here.');

  const scale = options.scale ?? 2;
  const background = options.background ?? palette.surface;

  const box = svg.getBoundingClientRect();
  const width = Math.round(box.width || Number(svg.getAttribute('width')) || 0);
  const height = Math.round(box.height || Number(svg.getAttribute('height')) || 0);
  if (!width || !height) {
    throw new Error('This chart has no size yet — let it finish drawing first.');
  }

  const clone = svg.cloneNode(true) as SVGSVGElement;
  inlineStyles(svg, clone);
  adoptSharedDefs(clone);
  clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  clone.setAttribute('width', String(width));
  clone.setAttribute('height', String(height));
  if (!clone.getAttribute('viewBox')) {
    clone.setAttribute('viewBox', `0 0 ${width} ${height}`);
  }

  const markup = new XMLSerializer().serializeToString(clone);
  // A data: URL rather than a blob: URL — Safari refuses to draw an image loaded
  // from a blob URL onto a canvas it will then read back, and this canvas is
  // read back.
  const source = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(markup)}`;

  const image = await new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('The chart image could not be rendered.'));
    img.src = source;
  });

  const canvas = document.createElement('canvas');
  canvas.width = width * scale;
  canvas.height = height * scale;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('This browser would not provide a drawing surface.');
  context.fillStyle = background;
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.drawImage(image, 0, 0, canvas.width, canvas.height);

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(blob => {
      if (blob) resolve(blob);
      else reject(new Error('The chart image could not be encoded.'));
    }, 'image/png');
  });
};

/** The same picture as a data URL, which is what jsPDF's addImage takes. */
export const svgToPngDataUrl = async (
  svg: SVGSVGElement | null,
  options: PngOptions = {}
): Promise<{ dataUrl: string; width: number; height: number }> => {
  const blob = await svgToPng(svg, options);
  const box = svg?.getBoundingClientRect();

  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('The chart image could not be read.'));
    reader.readAsDataURL(blob);
  });

  return {
    dataUrl,
    width: Math.round(box?.width || 0),
    height: Math.round(box?.height || 0),
  };
};

/**
 * Hand a blob to the browser as a download.
 *
 * ONE PLACE, because the object URL has to be revoked and the anchor removed,
 * and every copy of this in the codebase that forgot one of the two leaked.
 */
export const downloadBlob = (filename: string, blob: Blob): void => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
